import { avatarAnatomy } from "./componentAnatomyAvatar";
import { breadcrumbsAnatomy } from "./componentAnatomyBreadcrumbs";
import { buttonAnatomy } from "./componentAnatomyButton";
import { checkboxAnatomy } from "./componentAnatomyCheckbox";
import { fieldAnatomy } from "./componentAnatomyField";
import { inputAnatomy } from "./componentAnatomyInput";
import { kickLogoAnatomy } from "./componentAnatomyKickLogo";
import { linkAnatomy } from "./componentAnatomyLink";
import { menuAnatomy } from "./componentAnatomyMenu";
import { otpFieldAnatomy } from "./componentAnatomyOTPField";
import { textAreaAnatomy } from "./componentAnatomyTextArea";
import { pickerAnatomy } from "./componentAnatomyPicker";
import { radioAnatomy } from "./componentAnatomyRadio";
import { shortcutAnatomy } from "./componentAnatomyShortcut";
import { sideNavAnatomy } from "./componentAnatomySideNav";
import { switchAnatomy } from "./componentAnatomySwitch";
import { tagAnatomy } from "./componentAnatomyTag";
import { tagsInputAnatomy } from "./componentAnatomyTagsInput";
import { tooltipAnatomy } from "./componentAnatomyTooltip";
import type { DesignTokenTabId } from "./designTokensCatalog";

/**
 * Which design tokens paint which part of a component, per interaction state.
 *
 * The shape mirrors the CSS cascade rather than flattening it: a state only
 * lists what its own block declares, and everything else falls through to the
 * resting style. That is what lets the anatomy page say "`:hover` sets only
 * the border" instead of restating the whole component on every state.
 */

export type AnatomyStateId =
    | "resting"
    | "hover"
    | "pressed"
    | "focus"
    | "invalid"
    | "readOnly"
    | "disabled";

export interface AnatomyState {
    id: AnatomyStateId;
    label: string;
    /** Selector the component stylesheet uses to enter this state. */
    selector: string;
}

/** Visual pieces shared across the documented components. */
export type AnatomyPartId =
    | "halo"
    | "focusRing"
    | "border"
    | "background"
    | "fill"
    | "dot"
    | "glyph"
    | "thumb"
    | "spinner"
    | "grip"
    | "label"
    | "countChip"
    | "chip"
    | "xButton"
    | "surface"
    | "itemBackground"
    | "itemLabel"
    | "indicator"
    | "section"
    | "sectionTitle"
    | "separator"
    | "connector"
    | "optional"
    | "message"
    | "seam"
    | "popup"
    | "title"
    | "content"
    | "bullets"
    | "shortcut"
    | "tile"
    | "mark"
    | "wordmark";

export interface AnatomyPart {
    id: AnatomyPartId;
    label: string;
    /** Where the part lives in the component's markup or stylesheet. */
    element: string;
}

interface AnatomyBindingShape {
    partId: AnatomyPartId;
    /** CSS property, or a named slice of one when a property takes two tokens. */
    property: string;
    /** Component-scoped custom property the value is routed through. */
    via?: `--${string}`;
    note?: string;
}

export interface AnatomyTokenBinding extends AnatomyBindingShape {
    token: `--${string}`;
    literal?: undefined;
}

export interface AnatomyLiteralBinding extends AnatomyBindingShape {
    token?: undefined;
    /** Value the stylesheet declares outright, with no token behind it. */
    literal: string;
}

export type AnatomyBinding = AnatomyTokenBinding | AnatomyLiteralBinding;

export interface AnatomyVariant {
    id: string;
    label: string;
    /** Selector that selects this variant, appended to the spec's root selector. */
    selector: string;
    /** Resting bindings, layered over the spec's shared bindings. */
    restingBindings: readonly AnatomyBinding[];
    /** State blocks, layered over the resting bindings exactly as the cascade does. */
    stateBindings: Partial<Record<AnatomyStateId, readonly AnatomyBinding[]>>;
    /**
     * What a compound `.variant.size` block re-declares, keyed by size id. These
     * outrank the size axis, the way `.link.xs` outranks `.xs`.
     */
    sizeBindings?: Readonly<Record<string, readonly AnatomyBinding[]>>;
}

/**
 * One step of a component's size axis. Geometry and text style only: no colour
 * binding in these stylesheets varies by size, so the variants stay the single
 * home for the colour layer.
 */
export interface AnatomySize {
    id: string;
    label: string;
    /** Selector the stylesheet uses for this size, e.g. `.md`. */
    selector: string;
    bindings: readonly AnatomyBinding[];
}

export type AnatomySchematic =
    | "button"
    | "link"
    | "breadcrumbs"
    | "radio"
    | "checkbox"
    | "tag"
    | "input"
    | "otpField"
    | "textArea"
    | "field"
    | "picker"
    | "tagsInput"
    | "avatar"
    | "shortcut"
    | "tooltip"
    | "switch"
    | "menu"
    | "kickLogo"
    | "sideNav";

/** Components with a mined anatomy spec, keyed into `componentAnatomySpecs`. */
export type ComponentAnatomyId =
    | "button"
    | "link"
    | "breadcrumbs"
    | "radio"
    | "checkbox"
    | "tag"
    | "input"
    | "otpField"
    | "textArea"
    | "field"
    | "picker"
    | "tagsInput"
    | "avatar"
    | "shortcut"
    | "tooltip"
    | "switch"
    | "menu"
    | "kickLogo"
    | "sideNav";

export interface ComponentAnatomySpec {
    label: string;
    schematic: AnatomySchematic;
    rootSelector: string;
    /** Heading for the variant switcher, which differs per component. */
    variantAxisLabel: string;
    parts: readonly AnatomyPart[];
    states: readonly AnatomyState[];
    /** Bindings the component's own base block declares, whatever the size. */
    sharedBindings: readonly AnatomyBinding[];
    variants: readonly AnatomyVariant[];
    /** Omitted by components whose stylesheet has no size axis at all. */
    sizes?: readonly AnatomySize[];
}

export interface ResolvedAnatomyBinding {
    binding: AnatomyBinding;
    /** State block declaring this value; absent when it comes from the resting style. */
    setByStateId?: AnatomyStateId;
}

const bindingKey = (binding: AnatomyBinding) =>
    `${binding.partId}|${binding.property}`;

/**
 * Layers base → size → variant → `.variant.size` → state bindings in the order
 * the cascade resolves them: the stylesheets declare their sizes before their
 * variants, and compound blocks outrank both. Each property keeps its
 * first-seen position, so the legend reads in part order.
 */
export const resolveAnatomyBindings = ({
    spec,
    variant,
    size,
    stateId,
}: {
    spec: ComponentAnatomySpec;
    variant: AnatomyVariant;
    size?: AnatomySize;
    stateId: AnatomyStateId;
}): readonly ResolvedAnatomyBinding[] => {
    const resolved = new Map<string, ResolvedAnatomyBinding>();

    const layerBindings = (
        bindings: readonly AnatomyBinding[],
        setByStateId?: AnatomyStateId,
    ) => {
        bindings.forEach((binding) => {
            resolved.set(bindingKey(binding), { binding, setByStateId });
        });
    };

    layerBindings(spec.sharedBindings);

    if (size !== undefined) {
        layerBindings(size.bindings);
    }

    layerBindings(variant.restingBindings);

    if (size !== undefined) {
        layerBindings(variant.sizeBindings?.[size.id] ?? []);
    }

    if (stateId !== "resting") {
        layerBindings(variant.stateBindings[stateId] ?? [], stateId);
    }

    const partOrder = new Map(
        spec.parts.map((part, index) => [part.id, index] as const),
    );

    return [...resolved.values()].sort(
        (first, second) =>
            (partOrder.get(first.binding.partId) ?? 0) -
            (partOrder.get(second.binding.partId) ?? 0),
    );
};

export const anatomyBindingValue = (binding: AnatomyBinding): string =>
    binding.token === undefined ? binding.literal : `var(${binding.token})`;

/** Reads one property off a resolved state, for the schematic's inline styles. */
export const anatomyPartValue = ({
    bindings,
    partId,
    property,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    partId: AnatomyPartId;
    property: string;
}): string | undefined => {
    const match = bindings.find(
        (resolved) =>
            resolved.binding.partId === partId &&
            resolved.binding.property === property,
    );

    return match === undefined ? undefined : anatomyBindingValue(match.binding);
};

const tokenTabPrefixes: ReadonlyArray<{
    prefix: string;
    tabId: DesignTokenTabId;
}> = [
    { prefix: "--color-", tabId: "primitive-colors" },
    { prefix: "--space-", tabId: "spacing" },
    { prefix: "--border-width", tabId: "border-widths" },
    { prefix: "--radius-", tabId: "radius" },
    { prefix: "--shadow-", tabId: "shadows" },
    { prefix: "--font-", tabId: "font" },
    { prefix: "--tracking-", tabId: "font" },
];

/** Design-tokens tab holding a token's row, so a binding can deep-link to it. */
export const anatomyTokenTabId = (
    tokenName: `--${string}`,
): DesignTokenTabId => {
    const match = tokenTabPrefixes.find(({ prefix }) =>
        tokenName.startsWith(prefix),
    );

    return match?.tabId ?? "semantic-colors";
};

export const componentAnatomySpecs: Record<
    ComponentAnatomyId,
    ComponentAnatomySpec
> = {
    button: buttonAnatomy,
    link: linkAnatomy,
    breadcrumbs: breadcrumbsAnatomy,
    radio: radioAnatomy,
    checkbox: checkboxAnatomy,
    tag: tagAnatomy,
    input: inputAnatomy,
    otpField: otpFieldAnatomy,
    textArea: textAreaAnatomy,
    field: fieldAnatomy,
    picker: pickerAnatomy,
    tagsInput: tagsInputAnatomy,
    avatar: avatarAnatomy,
    shortcut: shortcutAnatomy,
    tooltip: tooltipAnatomy,
    switch: switchAnatomy,
    menu: menuAnatomy,
    kickLogo: kickLogoAnatomy,
    sideNav: sideNavAnatomy,
};
